import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Pagination from './Pagination';
import EmptyMessage from './EmptyMessage';

function columnAlignClass(align) {
  if (align === 'center') return ' text-center';
  if (align === 'right') return ' text-end';
  if (align === 'left') return ' text-start';
  return '';
}

function shouldSkipRowInteraction(target, currentTarget) {
  if (!target || typeof target.closest !== 'function') return false;
  const interactiveParent = target.closest('button, a, input, select, textarea, label, [role="button"], [data-row-click-ignore="true"]');
  if (!interactiveParent) return false;
  return interactiveParent !== currentTarget;
}

/**
 * 데이터테이블 A 컴포넌트
 * - items 전달 시: 정적 데이터 모드 (클라이언트 렌더링, 페이지네이션)
 * - items 미전달 시: API 모드 (DataTableList 외부 JS 사용)
 *
 * @example 정적 데이터
 * <DataTableA component={{
 *   id: 'my-list',
 *   items: list,
 *   columns: [{ key: 'no', label: '번호' }, { key: 'title', label: '제목', link: true }],
 *   getDetailPath: (item) => `/detail/${item.no}`,
 *   itemsPerPage: 10,
 *   showTotalCount: true,
 *   headerActions: [{ label: '추가', variant: 'primary', onClick: () => {} }]
 * }} />
 *
 * headerActions: 우측 상단 커스텀 버튼 배열. 각 항목: { key?, label, onClick?, variant?, className?, disabled? }
 * - variant: 'primary' | 'secondary' | 'outline-primary' | 'outline-secondary' | 'link' (Bootstrap btn 스타일)
 * rounded: 기본 true — 테이블 외곽 라운드(border-radius). 직각이 필요하면 rounded: false
 * totalCount: 정적 모드에서 "총 N건" 표시(서버 페이지네이션 시 API totalElements 등)
 * serverPagination: { currentPage: 0-based, totalPages, onPageChange } — 전달 시 items는 현재 페이지 행만 두고 하단 페이지네이션은 서버 기준
 * headerRight: React 노드 — "총 N건" 행 우측에 표시(headerActions보다 앞에 렌더)
 * columns[].align: 'left' | 'center' | 'right' — 셀 정렬(text-start / text-center / text-end)
 * columns[].headerAlign: th만 별도 정렬(미지정 시 align과 동일)
 * renderMobileItem: (row, index) => ReactNode — 정적 모드에서만, 992px 미만은 카드 / 이상은 테이블(전역 CSS의 .table-responsive 강제 display와 충돌하지 않도록 전용 클래스 사용)
 * mobileCard: 공통 모바일 카드 설정. enabled, title/topLeft/topRight/fields/actions 슬롯을 조합해 페이지별 카드형 UI를 선언적으로 구성
 * mobileCardDisabled: true 이면 정적 모드 기본 모바일 카드 자동 생성을 비활성화
 * 모바일(767px 이하): menu.css 가 table{display:block} 을 걸어도 본 컴포넌트 스타일로 table 레이아웃·가로 스크롤을 복구함. API 모드도 .table-responsive 로 감쌈.
 */
const DataTableA = ({ component, data }) => {
  const { t } = useTranslation();
  const comp = component || data;
  const headerRight = comp?.headerRight;
  const renderMobileItem = comp?.renderMobileItem;
  const mobileCard = comp?.mobileCard;
  const showTotalCount = comp?.showTotalCount !== false;
  const items = comp?.items;
  const isStaticMode = Array.isArray(items);
  const rounded = comp?.rounded !== false;
  const serverPagination = comp?.serverPagination;
  const skipClientSlice = Boolean(serverPagination);
  const mobileCardDisabled = comp?.mobileCardDisabled === true;

  const id = useMemo(() => comp?.id || `datatable-a-${Date.now()}`, [comp?.id]);
  const columns = useMemo(() => comp?.columns || [], [comp?.columns]);
  const itemsPerPage = comp?.itemsPerPage ?? 10;
  const getDetailPath = comp?.getDetailPath;
  const emptyText = comp?.emptyText;
  const onRowClick = comp?.onRowClick;
  const selectedId = comp?.selectedId;
  const getRowId = comp?.getRowId || ((row) => row.id);
  const getRowClassName = comp?.getRowClassName;
  const headerActions = useMemo(() => comp?.headerActions ?? [], [comp?.headerActions]);

  const [currentPage, setCurrentPage] = useState(0);

  const displayTotalCount = isStaticMode
    ? (comp?.totalCount != null ? Number(comp.totalCount) : items.length)
    : 0;
  const clientTotalPages = Math.max(1, Math.ceil(displayTotalCount / itemsPerPage));
  const effectiveTotalPages = serverPagination
    ? Math.max(1, Number(serverPagination.totalPages) || 1)
    : clientTotalPages;
  const paginatedItems = useMemo(() => {
    if (!isStaticMode || !items.length) return [];
    if (skipClientSlice) return items;
    const start = currentPage * itemsPerPage;
    return items.slice(start, start + itemsPerPage);
  }, [isStaticMode, items, currentPage, itemsPerPage, skipClientSlice]);

  // API 모드용 (훅은 항상 동일 순서로 호출)
  const apiUrl = useMemo(() => comp?.apiUrl || comp?.url || '/api/glow/bbsctt', [comp?.apiUrl, comp?.url]);
  const params = useMemo(() => comp?.params || comp?.queryParams || { bbsTpCd: 'fishery' }, [comp?.params, comp?.queryParams]);
  const tableInstanceRef = useRef(null);
  const initializedRef = useRef(false);
  const containerRef = useRef(null);
  const compRef = useRef(comp);

  useEffect(() => {
    setCurrentPage(0);
  }, [items]);

  useEffect(() => {
    compRef.current = comp;
  }, [comp]);

  const renderCellContent = useCallback((col, row, renderOpts) => {
    if (!col) return row ?? '-';
    const value = row?.[col.key];
    const customRender =
      typeof col.render === 'function' ? col.render(value, row, renderOpts || {}) : null;
    if (customRender !== null && customRender !== undefined) {
      return customRender;
    }
    const isLink = col.link && typeof getDetailPath === 'function';
    const href = isLink ? getDetailPath(row) : null;
    if (isLink && href) {
      return (
        <Link to={href} className="datatable-a-cell-link">
          {value}
        </Link>
      );
    }
    return value != null && value !== '' ? value : '-';
  }, [getDetailPath]);

  const resolveMobileCardValue = useCallback((slot, row, idx) => {
    if (!slot) return null;
    if (typeof slot.render === 'function') {
      const rendered = slot.render(row, idx);
      return rendered !== undefined ? rendered : null;
    }
    if (slot.key) {
      const matchedColumn = columns.find((col) => col.key === slot.key);
      if (matchedColumn) {
        return renderCellContent(matchedColumn, row, { variant: 'mobile' });
      }
      const raw = row?.[slot.key];
      return raw != null && raw !== '' ? raw : '-';
    }
    return null;
  }, [columns, renderCellContent]);

  const inferredMobileCard = useMemo(() => {
    if (!isStaticMode || mobileCardDisabled || renderMobileItem) {
      return null;
    }
    if (mobileCard && typeof mobileCard === 'object') {
      return mobileCard.enabled === false ? null : mobileCard;
    }

    const candidateColumns = columns.filter((col) => col?.key && col.key !== 'actions');
    if (candidateColumns.length === 0) {
      return null;
    }

    const titleColumn = candidateColumns.find((col) => col.link || col.align === 'left') || candidateColumns[0];
    const topRightColumn = candidateColumns.find((col) => col.key !== titleColumn?.key && /status|state|상태/i.test(`${col.key} ${col.label || ''}`));
    const topLeftColumn = candidateColumns.find((col) => col.key !== titleColumn?.key && col.key !== topRightColumn?.key);

    return {
      enabled: true,
      title: titleColumn ? { key: titleColumn.key } : undefined,
      topLeft: topLeftColumn ? { key: topLeftColumn.key } : undefined,
      topRight: topRightColumn ? { key: topRightColumn.key } : undefined,
      fields: candidateColumns
        .filter((col) => col.key !== titleColumn?.key && col.key !== topLeftColumn?.key && col.key !== topRightColumn?.key)
        .map((col) => ({ key: col.key, label: col.label, hideEmpty: false })),
      actions: columns.some((col) => col?.key === 'actions') ? { key: 'actions' } : undefined,
    };
  }, [columns, isStaticMode, mobileCard, mobileCardDisabled, renderMobileItem]);

  const resolvedMobileCard = inferredMobileCard;
  const hasBuiltInMobileCard = Boolean(resolvedMobileCard?.enabled);
  const hasMobileCardMode = Boolean(renderMobileItem || hasBuiltInMobileCard);

  const defaultMobileCardFields = useMemo(() => {
    if (!hasBuiltInMobileCard) return [];
    if (Array.isArray(resolvedMobileCard?.fields) && resolvedMobileCard.fields.length > 0) {
      return resolvedMobileCard.fields;
    }
    const excludedKeys = new Set([
      resolvedMobileCard?.title?.key,
      resolvedMobileCard?.topLeft?.key,
      resolvedMobileCard?.topRight?.key,
      'actions',
    ].filter(Boolean));
    return columns
      .filter((col) => col?.key && !excludedKeys.has(col.key))
      .map((col) => ({ key: col.key, label: col.label }));
  }, [columns, hasBuiltInMobileCard, resolvedMobileCard]);

  const builtInRenderMobileItem = useCallback((row, idx) => {
    if (!hasBuiltInMobileCard) return null;

    const titleContent = resolveMobileCardValue(resolvedMobileCard?.title, row, idx);
    const topLeftContent = resolveMobileCardValue(resolvedMobileCard?.topLeft, row, idx);
    const topRightContent = resolveMobileCardValue(resolvedMobileCard?.topRight, row, idx);
    const actionsContent = resolveMobileCardValue(resolvedMobileCard?.actions, row, idx);
    const cardClassName = ['datatable-a-mobile-card', resolvedMobileCard?.className].filter(Boolean).join(' ');
    const titleClassName = ['datatable-a-mobile-card-title', resolvedMobileCard?.title?.className].filter(Boolean).join(' ');
    const topLeftClassName = ['datatable-a-mobile-card-top-left', resolvedMobileCard?.topLeft?.className].filter(Boolean).join(' ');
    const topRightClassName = ['datatable-a-mobile-card-top-right', resolvedMobileCard?.topRight?.className].filter(Boolean).join(' ');
    const actionsClassName = ['datatable-a-mobile-card-actions', resolvedMobileCard?.actions?.className].filter(Boolean).join(' ');

    return (
      <article className={cardClassName}>
        {(topLeftContent !== null && topLeftContent !== undefined) || (topRightContent !== null && topRightContent !== undefined) ? (
          <div className="datatable-a-mobile-card-top">
            <div className={topLeftClassName}>{topLeftContent}</div>
            <div className={topRightClassName}>{topRightContent}</div>
          </div>
        ) : null}
        {titleContent !== null && titleContent !== undefined ? (
          <div className={titleClassName}>{titleContent}</div>
        ) : null}
        <div className="datatable-a-mobile-card-body">
          {defaultMobileCardFields.map((field, fieldIdx) => {
            const content = resolveMobileCardValue(field, row, idx);
            const isEmpty = content === null || content === undefined || content === '';
            if (field?.hideEmpty && isEmpty) return null;
            const fieldClassName = ['datatable-a-mobile-card-field', field?.className].filter(Boolean).join(' ');
            const labelClassName = ['datatable-a-mobile-card-field-label', field?.labelClassName].filter(Boolean).join(' ');
            const valueClassName = ['datatable-a-mobile-card-field-value', field?.valueClassName].filter(Boolean).join(' ');
            return (
              <div key={field?.key || fieldIdx} className={fieldClassName}>
                <span className={labelClassName}>{field?.label || ''}</span>
                <strong className={valueClassName}>{isEmpty ? '-' : content}</strong>
              </div>
            );
          })}
        </div>
        {actionsContent !== null && actionsContent !== undefined ? (
          <div className={actionsClassName}>{actionsContent}</div>
        ) : null}
      </article>
    );
  }, [defaultMobileCardFields, hasBuiltInMobileCard, resolveMobileCardValue, resolvedMobileCard]);

  const effectiveRenderMobileItem = renderMobileItem || builtInRenderMobileItem;

  const initializeTable = useCallback(async () => {
    if (isStaticMode) return;
    if (initializedRef.current) return;
    const element = containerRef.current || document.getElementById(id);
    if (!element) return;
    if (typeof window === 'undefined' || typeof window.DataTableList !== 'function') {
      console.error('[DataTableA] DataTableList is not available');
      return;
    }
    initializedRef.current = true;
    try {
      const table = new window.DataTableList(id, apiUrl, params, compRef.current);
      if (table?.init) {
        const r = table.init();
        if (r?.then) await r;
      }
      tableInstanceRef.current = table;
      window.__allTables = window.__allTables || {};
      window.__allTables[id] = table;
      if (table.onRowClick === undefined && compRef.current?.onRowClick) {
        table.onRowClick = compRef.current.onRowClick;
      }
    } catch (e) {
      console.error('[DataTableA] init error', e);
      initializedRef.current = false;
    }
  }, [id, apiUrl, params, isStaticMode]);

  useEffect(() => {
    if (isStaticMode) return;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeTable);
      return () => document.removeEventListener('DOMContentLoaded', initializeTable);
    }
    initializeTable();
    return () => {
      if (window.__allTables) delete window.__allTables[id];
      tableInstanceRef.current = null;
      initializedRef.current = false;
    };
  }, [initializeTable, id, isStaticMode]);

  // SearchFilter 연동 (정적 모드)
  useEffect(() => {
    if (!isStaticMode) return;
    const setSearchParams = comp?.setSearchParams;
    if (typeof setSearchParams === 'function') {
      window.__allTables = window.__allTables || {};
      window.__allTables[id] = { setSearchParams, init: () => setSearchParams({}) };
      return () => {
        if (window.__allTables) delete window.__allTables[id];
      };
    }
  }, [id, isStaticMode, comp?.setSearchParams]);

  const sharedStyles = (
    <style>{`
      .datatable-a.datatable-wrapper {
        width: 100%;
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
      }
      .datatable-a.datatable-wrapper .table-responsive {
        width: 100%;
        max-width: 100%;
      }
      .datatable-a.datatable-wrapper .table {
        width: 100%;
      }
      /*
       * public/assets/css/page/menu.css (max-width: 767px) 가
       * .table-responsive, table { display: block !important } 를 걸어
       * 테이블 레이아웃이 깨지고 가로 스크롤이 동작하지 않음.
       * DataTableA 범위에서만 table 을 display:table 로 복구하고 래퍼에 스크롤을 둠.
       */
      @media (max-width: 767.98px) {
        .datatable-a.datatable-wrapper .table-responsive {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          overflow-x: auto !important;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior-x: contain;
        }
        .datatable-a.datatable-wrapper .table-responsive table.table,
        .datatable-a.datatable-wrapper > table.table {
          display: table !important;
          width: max-content !important;
          min-width: 100% !important;
          max-width: none !important;
        }
      }
      .datatable-a.datatable-wrapper .table th,
      .datatable-a.datatable-wrapper .table td {
        border-left-color: transparent !important;
        border-right-color: transparent !important;
        border-bottom: 1px solid #eceff3 !important;
      }
      .datatable-a.datatable-wrapper .table-hover tbody tr:hover {
        background-color: color-mix(in srgb, var(--theme-color-primary, #5f5d5cff) 6%, white);
      }
      .datatable-a.datatable-wrapper tbody tr.datatable-a-row--selected {
        background-color: color-mix(in srgb, var(--theme-color-primary, #e97730) 14%, white);
        font-weight: 600;
      }
      .datatable-a.datatable-wrapper tbody tr.datatable-a-row--selected td {
        border-color: color-mix(in srgb, var(--theme-color-primary, #e97730) 30%, #dee2e6);
      }
      .datatable-a.datatable-wrapper tbody tr[role="button"] {
        cursor: pointer;
      }
      @media (max-width: 767.98px) {
        .datatable-a.datatable-wrapper .datatable-a-col-hide-mobile {
          display: none !important;
        }
      }
      .datatable-a-mobile-card-list {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }
      .datatable-a-mobile-card {
        display: grid;
        gap: 0.75rem;
        padding: 0.95rem;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        background: #ffffff;
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
      }
      .datatable-a-mobile-card-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
      }
      .datatable-a-mobile-card-top-left,
      .datatable-a-mobile-card-top-right {
        min-width: 0;
      }
      .datatable-a-mobile-card-title {
        font-size: 0.95rem;
        font-weight: 700;
        line-height: 1.45;
        color: #111827;
        word-break: keep-all;
      }
      .datatable-a-mobile-card-body {
        display: grid;
        gap: 0.5rem;
      }
      .datatable-a-mobile-card-field {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        font-size: 0.85rem;
      }
      .datatable-a-mobile-card-field-label {
        color: #6b7280;
        flex: 0 0 auto;
      }
      .datatable-a-mobile-card-field-value {
        color: #111827;
        font-weight: 600;
        text-align: right;
        min-width: 0;
      }
      .datatable-a-mobile-card-actions {
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
      }
      /*
       * renderMobileItem 사용 시: page/menu.css 가 (max-width:767px)에서 .table-responsive { display:block!important }
       * 를 걸어 Bootstrap d-none 이 무력화되며 767px 이하에서 테이블이 다시 보이는 문제가 있음.
       * 전용 클래스 + 높은 특이도로 lg 미만은 테이블 숨김 / lg 이상은 카드 숨김.
       */
      @media (max-width: 991.98px) {
        .datatable-a.datatable-wrapper.datatable-a--mobile-cards .datatable-a-table-desktop-only {
          display: none !important;
        }
        .datatable-a-mobile-card-actions > * {
          max-width: 100%;
        }
      }
      @media (min-width: 992px) {
        .datatable-a.datatable-wrapper.datatable-a--mobile-cards .datatable-a-mobile-card-list {
          display: none !important;
        }
        .datatable-a.datatable-wrapper.datatable-a--mobile-cards .datatable-a-table-desktop-only {
          display: block !important;
        }
      }
      .datatable-a-mobile-card-item.datatable-a-row--selected {
        outline: 2px solid var(--theme-color-primary, #e97730);
        outline-offset: 2px;
      }
      @media (max-width: 575.98px) {
        .datatable-a-mobile-card {
          padding: 0.85rem;
        }
        .datatable-a-mobile-card-top,
        .datatable-a-mobile-card-field,
        .datatable-a-mobile-card-actions {
          flex-wrap: wrap;
        }
        .datatable-a-mobile-card-actions {
          justify-content: stretch;
        }
        .datatable-a-mobile-card-actions > * {
          width: 100%;
        }
      }
      .datatable-a.datatable-wrapper .total-count {
        font-weight: 600;
        color: var(--theme-color-primary, #e97730);
      }
      .datatable-a-cell-link {
        color: #222;
        text-decoration: none;
      }
      .datatable-a-cell-link:hover {
        color: var(--theme-color-primary, #e97730);
      }
      .datatable-a-empty-cell {
        padding: 0 !important;
        vertical-align: middle;
        border-left: 1px solid #dee2e6;
        border-right: 1px solid #dee2e6;
      }
      .datatable-a.datatable-wrapper .datatable-a-header-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: center;
      }
      /* rounded 옵션: 테이블 외곽 라운드 (overflow:hidden 은 모바일 가로 스크롤을 막음) */
      .datatable-a--rounded.datatable-wrapper .table-responsive {
        border-radius: 8px;
        overflow-x: auto;
        overflow-y: hidden;
        border: 1px solid #e8e7e8;
      }
      .datatable-a--rounded.datatable-wrapper .table {
        border-radius: 8px;
        margin-bottom: 0;
      }
      .datatable-a--rounded.datatable-wrapper thead tr:first-child th:first-child {
        border-top-left-radius: 7px;
      }
      .datatable-a--rounded.datatable-wrapper thead tr:first-child th:last-child {
        border-top-right-radius: 7px;
      }
      .datatable-a--rounded.datatable-wrapper tbody tr:last-child td:first-child {
        border-bottom-left-radius: 7px;
      }
      .datatable-a--rounded.datatable-wrapper tbody tr:last-child td:last-child {
        border-bottom-right-radius: 7px;
      }
      .datatable-a--rounded.datatable-wrapper tbody tr:last-child td.datatable-a-empty-cell {
        border-bottom-left-radius: 7px;
        border-bottom-right-radius: 7px;
      }
    `}</style>
  );

  // ----- 정적 데이터 모드 -----
  if (isStaticMode) {
    return (
      <>
        <section
          id={id}
          className={`datatable-a datatable-wrapper my-2${rounded ? ' datatable-a--rounded' : ''}${hasMobileCardMode ? ' datatable-a--mobile-cards' : ''}`}
        >
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              {showTotalCount && (
                <span className="text-secondary small">
                  {t('cardList.totalCount', { count: displayTotalCount.toLocaleString() })}
                </span>
              )}
            </div>
            {(headerActions.length > 0 || headerRight != null) && (
              <div className="datatable-a-header-actions d-flex flex-wrap align-items-center gap-2 justify-content-end ms-auto">
                {headerRight}
                {headerActions.map((action, idx) => {
                  const variant = action.variant ?? 'secondary';
                  const btnClass = `btn btn-${variant} ${action.className ?? ''}`.trim();
                  return (
                    <button
                      key={action.key ?? `header-action-${idx}`}
                      type="button"
                      className={btnClass}
                      disabled={action.disabled}
                      onClick={typeof action.onClick === 'function' ? action.onClick : undefined}
                    >
                      {action.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className={`table-responsive${hasMobileCardMode ? ' datatable-a-table-desktop-only' : ''}`}>
            <table className="table table-bordered table-hover align-middle">
              <thead>
                <tr className="datatable-header">
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={`${columnAlignClass(col.headerAlign ?? col.align).trim()}${col.hideOnMobile ? ' datatable-a-col-hide-mobile' : ''}`}
                      style={col.width ? { width: col.width } : undefined}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedItems.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="datatable-a-empty-cell">
                      <EmptyMessage text={emptyText} variant="inline" />
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map((row, idx) => {
                    const rowId = getRowId(row);
                    const isSelected = selectedId != null && rowId != null && selectedId === rowId;
                    const isClickable = typeof onRowClick === 'function';
                    const customRowClass = typeof getRowClassName === 'function' ? getRowClassName(row) : '';
                    const rowClassName = [isSelected ? 'datatable-a-row--selected' : '', customRowClass].filter(Boolean).join(' ');
                    return (
                      <tr
                        key={row.no != null ? `${row.no}-${idx}` : rowId != null ? `${rowId}-${idx}` : idx}
                        className={rowClassName || undefined}
                        role={isClickable ? 'button' : undefined}
                        tabIndex={isClickable ? 0 : undefined}
                        onClick={isClickable ? () => onRowClick(row) : undefined}
                        onKeyDown={
                          isClickable
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onRowClick(row);
                                }
                              }
                            : undefined
                        }
                      >
                        {columns.map((col) => {
                          return (
                            <td
                              key={col.key}
                              className={`${columnAlignClass(col.align).trim()}${col.hideOnMobile ? ' datatable-a-col-hide-mobile' : ''}`}
                              onClick={col.key !== 'actions' ? undefined : (e) => e.stopPropagation()}
                            >
                              {renderCellContent(col, row, { variant: 'table' })}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {hasMobileCardMode ? (
            <div className="datatable-a-mobile-card-list">
              {paginatedItems.length === 0 ? (
                <div className="border rounded py-4 px-2 bg-light">
                  <EmptyMessage text={emptyText} variant="inline" />
                </div>
              ) : (
                paginatedItems.map((row, idx) => {
                  const rowId = getRowId(row);
                  const key = row.no != null ? `${row.no}-${idx}` : rowId != null ? `${rowId}-${idx}` : idx;
                  const isSelected = selectedId != null && rowId != null && selectedId === rowId;
                  const isClickable = typeof onRowClick === 'function';
                  const customRowClass = typeof getRowClassName === 'function' ? getRowClassName(row) : '';
                  const rowClassName = ['datatable-a-mobile-card-item', isSelected ? 'datatable-a-row--selected' : '', customRowClass].filter(Boolean).join(' ');
                  return (
                    <div
                      key={key}
                      className={rowClassName || undefined}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      onClick={isClickable ? (e) => {
                        if (shouldSkipRowInteraction(e.target, e.currentTarget)) return;
                        onRowClick(row);
                      } : undefined}
                      onKeyDown={isClickable ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(row);
                        }
                      } : undefined}
                    >
                      {effectiveRenderMobileItem(row, idx)}
                    </div>
                  );
                })
              )}
            </div>
          ) : null}

          {effectiveTotalPages > 1 && (
            <div className="pagination-wrapper mt-3 text-center">
              <Pagination
                currentPage={serverPagination ? serverPagination.currentPage : currentPage}
                totalPages={effectiveTotalPages}
                onPageChange={serverPagination ? serverPagination.onPageChange : setCurrentPage}
              />
            </div>
          )}
        </section>
        {sharedStyles}
      </>
    );
  }

  // ----- API 모드: DataTableList 사용 -----
  return (
    <>
      <section ref={containerRef} id={id} className={`datatable-a datatable-wrapper my-2${rounded ? ' datatable-a--rounded' : ''}`}>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            {showTotalCount && (
              <span className="text-secondary small">
                {t('cardList.totalCount', { count: '0' })}
              </span>
            )}
          </div>
          {(headerActions.length > 0 || headerRight != null) && (
            <div className="datatable-a-header-actions d-flex flex-wrap align-items-center gap-2 justify-content-end ms-auto">
              {headerRight}
              {headerActions.map((action, idx) => {
                const variant = action.variant ?? 'secondary';
                const btnClass = `btn btn-${variant} ${action.className ?? ''}`.trim();
                return (
                  <button
                    key={action.key ?? `header-action-${idx}`}
                    type="button"
                    className={btnClass}
                    disabled={action.disabled}
                    onClick={typeof action.onClick === 'function' ? action.onClick : undefined}
                  >
                    {action.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle">
            <thead>
              <tr className="datatable-header"></tr>
            </thead>
            <tbody></tbody>
          </table>
        </div>
        <div className="pagination-wrapper mt-3 text-center"></div>
      </section>
      {sharedStyles}
    </>
  );
};

export default DataTableA;
